import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { phCapture } from '@/lib/analytics/posthog';
import { STRINGS } from '@/strings/strings';
import { AnchorReviewScreen } from '../components/anchor/AnchorReviewScreen';
import { BestellungScreen } from '../components/bestellung/BestellungScreen';
import { BookStatusInfoScreen } from '../components/BookStatusInfoScreen';
import { GeschichteScreen } from '../components/geschichte/GeschichteScreen';
import { ImageRefusedScreen } from '../components/image-refused/ImageRefusedScreen';
import { PhotoRejectedScreen } from '../components/photo-rejected/PhotoRejectedScreen';
import { BlockDerivationRejectedScreen } from '../components/block-derivation-rejected/BlockDerivationRejectedScreen';
import { PostOrderView } from '../components/post-order/PostOrderView';
import { PostPaymentWaitScreen } from '../components/PostPaymentWaitScreen';
import { PreviewScreen } from '../components/preview/PreviewScreen';
import { ReviewScreen } from '../components/review/ReviewScreen';
import { SummaryReviewScreen } from '../components/summary-review/SummaryReviewScreen';
import {
  fireBookOrderPaidOnce,
  isBookRouteFreshPaidFireStatus,
} from '../book-order-paid-sentinel';
// PreviewWaitScreen deliberately not imported — Sebastian ruling
// 2026-08-12 evening removed the preview_rendering → PreviewWaitScreen
// dispatch. The component file remains until this flip is verified in
// production; its deletion is a follow-up commit.
import { WaitScreen } from '../components/WaitScreen';
import { useBookShellContext } from '../book-shell-context';
import { mapStatusToBuilderStep } from '../status-to-step';

/**
 * Book-funnel PostHog helper — fires a phCapture event once per scope
 * per browser session/orderId. `phCapture` returns `false` when
 * PostHog is not yet initialised (statistics consent not granted), so
 * the sentinel is only written on a real emission — the event will
 * fire later when the customer consents. All storage access is
 * try/catch'd; storage-access failures and phCapture errors are
 * non-fatal. NEVER include PII (child/cast names, story text, photos,
 * emails) in the `properties` payload.
 */
function fireBookAnalytics(
  event: string,
  dedupKey: string,
  storage: 'session' | 'local',
  properties?: Record<string, unknown>,
): void {
  try {
    const store = storage === 'session' ? window.sessionStorage : window.localStorage;
    if (store.getItem(dedupKey) === '1') return;
    if (phCapture(event, properties)) {
      store.setItem(dedupKey, '1');
    }
  } catch {
    // storage access failures + phCapture errors are non-fatal
  }
}

/**
 * Route entry at `/book/:orderId` (Bau-Spez v3.1 §7 — the one route,
 * status decides which screen renders).
 *
 * Reads the envelope from the shell via `useBookShellContext()`
 * (`../book-shell-context.tsx`) so the fetch is deduplicated with the
 * Schrittleiste. Dispatches on `envelope.status`. Every value of the
 * backend enum `BookOrderStatus` (`prisma/schema.prisma`) has a
 * branch except `draft_configuring`, which never routes to
 * `/book/:orderId` (the configurator lives at `/book`) and falls to
 * the default. Block 2 commit 2a (Sebastian ruling 2026-08-31 evening)
 * added the eleven post-render + post-checkout branches — see the
 * comment block above the `print_approved` case for the string-group
 * table.
 *
 * ═══ WHY THIS ROUTE HOLDS NO OWN FETCH ═══════════════════════════════
 *
 * BookShell already fetches via `useReentryStep`, which internally
 * calls `usePreviewEnvelope`. If this route also called the hook,
 * two independent instances would each own their own state and each
 * fetch on mount. Outlet context is the single-fetch path, and the
 * `refetch()` callback surfaced through the context lets a mutation
 * here (title save, page retry) request a refresh without duplicate
 * plumbing.
 *
 * Loading and error states are also surfaced from the shell so this
 * route can render a synchronous fallback — the shell chrome
 * (Kopfleiste, Bahn) is already visible when this component mounts.
 */
export function BookRoute() {
  const { orderId } = useParams<{ orderId: string }>();
  const { envelope, loading, error, refetch } = useBookShellContext();

  // Sebastian ruling 2026-09-18 — the book flow lives at one route
  // (`/book/:orderId`); the "screen" is a switch on `envelope.status`.
  // `pwa/src/components/ScrollToTop.tsx` resets scroll on `pathname`
  // change, so a status transition (e.g. summary_review → anchor_pending)
  // leaves the customer at the previous screen's scroll offset. Reset
  // when `envelope?.status` changes so a new screen starts at the top.
  // Fires ONLY on status transitions — not on in-screen mode changes
  // within the same status, not on every re-render, and not while
  // loading (envelope === null → status is undefined → effect skipped).
  const status = envelope?.status;
  useEffect(() => {
    if (status === undefined) return;
    window.scrollTo(0, 0);
  }, [status]);

  // Book-funnel analytics — events 5/6/7 (`book_story_step_reached`,
  // `book_figures_reached`, `book_preview_seen`). Fires the first
  // time the builder step reaches 2/3/4 for a given orderId.
  // Order-scoped dedup via localStorage so a customer visiting the
  // same order in a later session does not refire. NEVER include
  // story text or cast names — only the numeric step + orderId.
  useEffect(() => {
    if (status === undefined || orderId === undefined) return;
    const step = mapStatusToBuilderStep(status);
    if (step.kind === 'step') {
      if (step.value === 2) {
        fireBookAnalytics(
          'book_story_step_reached',
          `book_story_step_reached_${orderId}`,
          'local',
          { book_order_id: orderId, builder_step: 2 },
        );
      } else if (step.value === 3) {
        fireBookAnalytics('book_figures_reached', `book_figures_reached_${orderId}`, 'local', {
          book_order_id: orderId,
          builder_step: 3,
        });
      } else if (step.value === 4) {
        fireBookAnalytics('book_preview_seen', `book_preview_seen_${orderId}`, 'local', {
          book_order_id: orderId,
          builder_step: 4,
        });
      }
    }
    // Sebastian ruling 2026-10-08 — Freigabe-step enters the funnel.
    // `customer_review` is the only status mapping to
    // `post_payment_approval`; the mapper's existing branch keeps the
    // dedup aligned with the status itself (one event per orderId per
    // transition).
    if (step.kind === 'post_payment_approval') {
      fireBookAnalytics(
        'book_customer_review_reached',
        `book_customer_review_reached_${orderId}`,
        'local',
        { book_order_id: orderId },
      );
    }
  }, [status, orderId]);

  // Sebastian ruling 2026-10-22 (gate tightened 2026-10-27) — paid-
  // acknowledgment fire-once from the book route. `/erfolg` already
  // fires `book_order_paid` on its success path, but customers reach
  // `/book/:orderId` in a paid status through routes that bypass
  // `/erfolg` entirely: closed the Stripe tab mid-webhook, authenticated
  // elsewhere after paying, or hard-reloaded before `/erfolg` finished
  // polling. The sentinel in `book-order-paid-sentinel.ts` is per-order
  // localStorage, so whichever route fires first wins; the second
  // no-ops.
  //
  // Gate = `BOOK_ROUTE_FRESH_PAID_FIRE_STATUSES` (paid, rendering_full,
  // qa_running, qa_regen, qa_human_queue, customer_review). NARROWER
  // than `BOOK_PAID_OR_BEYOND_STATUSES` because the envelope exposes no
  // `paidAt` and firing on `shipped`/`delivered` for a customer opening
  // her review-mail link weeks later would retro-date a second
  // conversion event. The cut-off is `customer_review` (inclusive) —
  // post-release statuses never qualify as a fresh-fire moment. See the
  // sentinel's JSDoc for the full rationale.
  //
  // Visible acknowledgment (cover + 4 preview tiles + "Vielen Dank") is
  // already rendered by `PostPaymentWaitScreen` for paid/rendering_full/
  // qa_running and by `ReviewScreen` for customer_review, so there is
  // no new copy here — this effect is the funnel completion signal
  // only. `salesUnit` is `null` because the shell envelope does not
  // expose it; the backend keeps the split via amount tracking.
  useEffect(() => {
    if (status === undefined || orderId === undefined) return;
    if (!isBookRouteFreshPaidFireStatus(status)) return;
    fireBookOrderPaidOnce(orderId, null);
  }, [status, orderId]);

  // Sebastian ruling 2026-09-24 (Meine Bücher → finished book) —
  // pre-ruling this branch rendered an aria-hidden empty div in
  // BOTH loading and error paths. Rationale (mine, wrong): the
  // shell chrome was visible so the customer had "something to
  // look at". iPhone testing showed the customer stares at a blank
  // content area for 6-30 seconds after tapping a finished book,
  // reads it as "the page is broken" and taps back. Now:
  //   - LOADING → WaitScreen with the approved copy + centred
  //     spinner (STRINGS.book.postOrder.opening). The wording is
  //     optimised for the finished-book case (the dominant
  //     cold-load post-launch: tap in Meine Bücher, click a
  //     shipment email); other cold-loads (URL bookmark of a
  //     mid-flow book, page reload during creation) see it briefly
  //     before the envelope arrives and the correct wait screen
  //     takes over.
  //   - ERROR / envelope === null (post-error) → keep the
  //     aria-hidden placeholder as before. The shell's
  //     BookErrorHandler surfaces the failure through the shell
  //     chrome (P5-4 Commit A); a visible error message here would
  //     duplicate it.
  if (loading) {
    return <WaitScreen heading={STRINGS.book.postOrder.opening} showSpinner centered />;
  }
  if (error !== null || envelope === null) {
    return <div data-testid="book-route" data-order-id={orderId ?? ''} aria-hidden="true" />;
  }

  switch (envelope.status) {
    case 'light_gate_pending':
      // Position-5 Commit 6b.4 (Sebastian ruling 2026-09-02) — the
      // pre-summary wait is block-derivation + the pre-model, NOT the
      // anchor render. Reuses the summary-review "generating" copy
      // which describes what is actually happening at this status: we
      // are reading the input and preparing the summary.
      //
      // Block 3 correction (Sebastian test-book finding 2026-09-09) —
      // drops the pre-fix `teaserLead: wait.teaserLead` which was
      // rendering "Einen Moment noch. Wusstest du das schon?" below
      // the heading on this screen (a leftover cross-wired from the
      // anchor-pending wait). Now uses `body: summaryReview
      // .generatingBody` = "Das dauert einen kurzen Moment.", plus
      // `showSpinner=true` for a visible motion cue and
      // `centered=true` so the small block sits in the middle of the
      // BookLane card rather than hugging the top of a large empty
      // white area. Beruhigungszeile stays per Sebastian's ruling
      // ("was not part of the spec but it is fine — leave it").
      return (
        <WaitScreen
          heading={STRINGS.book.summaryReview.generatingHeading}
          body={STRINGS.book.summaryReview.generatingBody}
          showSpinner
          centered
          beruhigungszeile={STRINGS.book.wait.beruhigungszeile}
        />
      );
    case 'summary_review':
      // Position-5 Commit 6b (Sebastian ruling 2026-09-02) — customer
      // reads the pre-model summary, retries up to 3 times, releases
      // (or self-writes from run 3). Screen owns all 7 sub-states
      // (generating / ready-to-review / retry-in-flight / retry-failed
      // / cap-reached / self-write-in-flight / generation-failed);
      // envelope.summaryReview drives the branching.
      return <SummaryReviewScreen envelope={envelope} onRefetch={refetch} />;
    case 'anchor_pending':
      // Schritt 2 wait — state 1 vs state 5 heading derived from
      // `cover.remainingAnchor` per Sebastian ruling 2026-08-05 §2:
      //   remainingAnchor === 2 → attemptNo 1 pending (state 1)
      //   remainingAnchor  < 2  → attemptNo >= 2 pending (state 5)
      // Reload-safe by construction — the envelope carries the signal.
      //
      // Slow-copy line (Sebastian ruling 2026-09-08 evening, Commit 3
      // of A+B+C) — reveals after 4 minutes on this wait screen so
      // the customer knows something is off before the release-chain
      // BullMQ retries exhaust (worst case ~8 min per Commit 1
      // sandwich). Same threshold + wording as story_generating /
      // anchor_approved (both wired in c69d201). Deliberately NOT
      // firing on the normal anchor-render happy path (90-150 s
      // observed) — 4 minutes reads as "definitely longer than
      // usual" without triggering on typical variance.
      return (
        <WaitScreen
          heading={
            // Sebastian ruling 2026-09-11 SLICE D — voluntary photo swap
            // in flight (customer clicked "Foto ändern" on the anchor
            // screen; backend transitioned anchor_review → anchor_pending
            // and is re-deriving + re-rendering). Overrides both the
            // initial-render (remainingAnchor === 2) and regen headings.
            envelope.voluntaryPhotoSwapInFlight === true
              ? STRINGS.book.voluntaryPhotoSwap.loadingLine
              : envelope.cover.remainingAnchor === 2
                ? STRINGS.book.wait.anchorHeading
                : STRINGS.book.wait.regenHeading
          }
          // Block 4 · 4.1 (Sebastian ruling 2026-09-09) — the initial
          // anchor render (remainingAnchor === 2) gets a full-loader
          // shape: heading + body + spinner, centered vertically.
          //
          // Sebastian ruling 2026-09-15 reverses the earlier decision
          // that kept the regen path spinner-less: the "Wir zeichnen
          // neu." wait now renders the same spinner (Loader2, primitive
          // `showSpinner: true`) so the customer has a visible motion
          // cue while a regen runs. Body copy stays only on the initial
          // anchor render — pre-Block-4 shape for the regen preserved
          // there.
          //
          // Same slow-copy timer applies to both branches (4-min
          // threshold, Sebastian ruling 2026-09-08). teaserLead
          // ("Einen Moment noch. Wusstest du das schon?") dropped —
          // Sebastian ruling explicitly removes it.
          {...(envelope.cover.remainingAnchor === 2
            ? {
                body: STRINGS.book.wait.anchorBody,
                showSpinner: true,
                centered: true,
              }
            : {
                showSpinner: true,
                centered: true,
              })}
          beruhigungszeile={STRINGS.book.wait.beruhigungszeile}
          slowCopy={STRINGS.book.wait.storySlowCopy}
          slowThresholdMs={240000}
        />
      );
    case 'anchor_review':
      return <AnchorReviewScreen envelope={envelope} onRefetch={refetch} />;
    case 'story_generating':
      // Schritt 3 wait — heading + body released 2026-09-10 late
      // evening (Sebastian). Same layout as the initial-anchor wait
      // (Block 4 · 4.1): heading + body + spinner, centred vertically
      // so the small block sits in the middle of the BookLane card
      // rather than hugging the top. Drops the pre-fix teaserLead
      // ("Einen Moment noch. Wusstest du das schon?"), which was the
      // intro to a Kinderfrage-Karte (Phase-6+ deferred content
      // pipeline never built — see WaitScreen.tsx:14-19). Same fix
      // as the Block 3 correction Sebastian made on light_gate_pending
      // for the same "cross-wired leftover" reason.
      //
      // Slow-copy line (Sebastian ruling 2026-09-08) — reveals after
      // 4 minutes to acknowledge the wait honestly. Threshold picked
      // from measured production durations (story 0:45-1:58 typical);
      // 240000 ms reads as "definitely longer than usual" without
      // firing on the normal upper-bound of story-gen.
      return (
        <WaitScreen
          heading={STRINGS.book.wait.storyHeading}
          body={STRINGS.book.wait.storyBody}
          showSpinner
          centered
          beruhigungszeile={STRINGS.book.wait.beruhigungszeile}
          slowCopy={STRINGS.book.wait.storySlowCopy}
          slowThresholdMs={240000}
        />
      );
    case 'anchor_approved':
      // Sebastian ruling 2026-08-05 (v3 §7 line 411, cited verbatim
      // in status-to-step.ts:32-37): "anchor_approved gehört auf
      // Geschichte, nicht ins Leere: nach J-05 ist der Accept am
      // Anker-Gate das Ereignis, das die Geschichte anstößt. Wer in
      // dieser Sekunde neu lädt, darf keine Leiste ohne aktive Marke
      // sehen."
      //
      // Same WaitScreen as story_generating — anchor_approved is the
      // transient state between anchor accept and the story processor
      // picking the job off Redis. Latent bug 2026-08-12 → 2026-08-28:
      // this case was missing from the switch AND from use-preview-
      // envelope.ts's NON_TERMINAL_POLLING_STATES. A customer whose
      // refetch-after-accept landed while the backend was still on
      // anchor_approved fell to the (silent) default, and polling
      // never restarted because the polling list didn't cover it.
      // The default is now loud; both surfaces fixed in the same
      // commit.
      //
      // Same-shape change as story_generating (Sebastian ruling
      // 2026-09-10 late evening): drop teaserLead, add body + spinner
      // + centered. anchor_approved renders byte-identical to
      // story_generating because the customer's mental model in both
      // is "story is coming" — one is transient (job hand-off), one
      // is the actual gen. Applying the fix to only one would ship
      // the defect on the sibling during the anchor_approved window.
      return (
        <WaitScreen
          heading={STRINGS.book.wait.storyHeading}
          body={STRINGS.book.wait.storyBody}
          showSpinner
          centered
          beruhigungszeile={STRINGS.book.wait.beruhigungszeile}
          slowCopy={STRINGS.book.wait.storySlowCopy}
          slowThresholdMs={240000}
        />
      );
    case 'text_editing':
      return <GeschichteScreen envelope={envelope} onRefetch={refetch} />;
    case 'preview_rendering':
    case 'preview_ready':
      // Sebastian ruling 2026-08-12 evening — dispatch flip. Both
      // preview states now render PreviewScreen. Under
      // preview_rendering: the anchor tile (imageUrl populated since
      // Schritt 2) fills immediately; the four scene tiles show
      // state='rendering' with spinner + copy; each tile flips
      // rendering→ready as its render completes (envelope polled
      // every 5s via usePolling in usePreviewEnvelope). Under
      // preview_ready: all four tiles are ready. Same screen, tile
      // states carry the progress — no full-screen wait covering
      // the anchor image the customer has been waiting to see.
      // PreviewWaitScreen is now unreferenced; deletion follows in
      // a separate commit once this dispatch flip is verified in
      // production (Sebastian ruling).
      return <PreviewScreen envelope={envelope} onRefetch={refetch} />;
    case 'checkout_pending':
      // 6-3 Bildschirm 5 · Bestellung (Sebastian ruling 2026-08-15).
      // Re-entry after Stripe abandonment lands here — customer
      // clicked "back" from Stripe's hosted-page, or closed the tab
      // and returned via /book/:orderId. Render BestellungScreen
      // inline so the customer can resume: BestellungScreen's
      // Jetzt-bestellen calls createBookCheckout again, and the
      // service's duplicate-guard returns the existing open
      // session's URL (idempotent resume) or rolls back on expired.
      return <BestellungScreen envelope={envelope} />;
    case 'photo_rejected':
      // Coordination commit 2026-08-06: photo failed multi-face
      // check. Screen shows the rejected cast + slot + swap flow.
      // Schrittleiste marker is Personen (Bar A step 1) via the
      // `photo_rejected` kind on `BookBuilderStep`.
      return <PhotoRejectedScreen envelope={envelope} onRefetch={refetch} />;
    case 'block_derivation_rejected':
      // 2026-10-14 — per-figure block-derivation rejection (vehicle
      // entered as tier figure, person photo not derivable). Screen
      // reads envelope.intake.derived.rejections and shows a hint +
      // photo-swap affordance per failed figure.
      return <BlockDerivationRejectedScreen envelope={envelope} onRefetch={refetch} />;
    case 'paid':
    case 'rendering_full':
    case 'qa_running':
      // Block 6 · 6.7 (Sebastian ruling 2026-09-09) — post-payment
      // wait rewritten. Single screen for all three statuses; the
      // screen shows heading + body + anchor image + 4 preview
      // tiles + three lines. NO progress bar, NO stage-dot
      // indicator. Envelope threaded through so the screen can
      // render the anchor image + tile row. Bar B (Bestellung
      // DONE, Freigabe OFFEN) renders separately via Schrittleiste.
      return <PostPaymentWaitScreen envelope={envelope} />;
    case 'customer_review':
      // 8a customer review screen (Sebastian ruling 2026-08-17).
      // 21 slots: cover + 20 pages, horizontal chevron paging.
      // Release button appears at the last slot only.
      return <ReviewScreen envelope={envelope} onRefetch={refetch} />;
    // Block 2 commit 2a (Sebastian ruling 2026-08-31 evening) —
    // eleven post-render + post-checkout branches so no customer sees
    // "Wir zeichnen dein Titelbild." on a state where nothing is
    // being drawn. All read-only; BookStatusInfoScreen renders
    // heading + subtitle only, no CTA. Six string groups, some shared
    // across statuses per Sebastian's copy:
    //   printApproved: print_approved, prepress, handoff_pod
    //   shipped:       shipped
    //   delivered:     delivered
    //   qaRegen:       qa_regen, qa_human_queue
    //   cancelled:     cancelled
    //   pausedBilling: paused_billing, reclamation_open
    // Sebastian ruling 2026-09-24 — read-only book viewer supersedes
    // the previous "thank-you-only" BookStatusInfoScreen dispatch for
    // the five post-approval statuses. Customers coming back from
    // "Meine Bücher" see their finished book (the same page renderer
    // as customer_review) with mutation controls hidden. Status line
    // above the pages: Im Druck (print_approved / prepress /
    // handoff_pod) / Versendet (shipped) / Zugestellt (delivered).
    // The pre-2026-09-24 `status.printApproved` / `status.shipped` /
    // `status.delivered` strings stay in strings.ts as dead code —
    // launch-safe non-removal, cleanup follows separately.
    case 'print_approved':
    case 'prepress':
    case 'handoff_pod':
    case 'shipped':
    case 'delivered':
      return <PostOrderView envelope={envelope} onRefetch={refetch} />;
    case 'qa_regen':
    case 'qa_human_queue':
      return (
        <BookStatusInfoScreen
          heading={STRINGS.book.status.qaRegen.heading}
          subtitle={STRINGS.book.status.qaRegen.subtitle}
        />
      );
    case 'cancelled':
      return (
        <BookStatusInfoScreen
          heading={STRINGS.book.status.cancelled.heading}
          subtitle={STRINGS.book.status.cancelled.subtitle}
        />
      );
    case 'paused_billing':
    case 'reclamation_open':
      return (
        <BookStatusInfoScreen
          heading={STRINGS.book.status.pausedBilling.heading}
          subtitle={STRINGS.book.status.pausedBilling.subtitle}
        />
      );
    // Sebastian ruling 2026-09-08 evening (Commit 2 of A+B+C) —
    // release-chain terminal-fail. Reached when the backend's
    // BookReleaseChainProcessor catch flipped anchor_pending →
    // book_release_chain_error (Anthropic 529 outlasting Commit 1's
    // extended retry window, or a persistent SDK error). Renders the
    // released copy — heading + three paragraphs — via the array-
    // subtitle branch of BookStatusInfoScreen. Customer waits for
    // the resume mail (Commit 3, Promise 2); no CTA here, no action
    // to take. Polled in NON_TERMINAL_POLLING_STATES so an operator
    // recovery lands on the customer's screen without a manual
    // refresh.
    case 'book_release_chain_error':
      return (
        <BookStatusInfoScreen
          heading={STRINGS.book.status.releaseChainError.heading}
          subtitle={STRINGS.book.status.releaseChainError.paragraphs}
        />
      );
    // Sebastian ruling 2026-09-10 (Paket 3) — image-refusal state.
    // Reached when the OpenAI image endpoint returns a 2xx-with-
    // error content refusal (`RenderContentRefusalError`) on a render
    // call. Backend catches, transitions the order to `image_refused`,
    // and persists the stage on `intake.derived.imageRefusal`. Stage
    // discriminator selects the customer-facing copy — same shared
    // support line under all three stages. The refusal reason from
    // OpenAI is NEVER shown (Sebastian: English policy message, may
    // name things the parent does not want to read).
    //
    // Paket 3 Item 1 (Sebastian ruling 2026-09-10): recovery
    // affordance landed for `book_anchor` stage — `ImageRefusedScreen`
    // renders header + per-cast photo-swap rows so the customer can
    // swap any face photo to unstick the book. Cover + scene stages
    // (forward-compat, not firing today) render header only until
    // items 2 + 3 wire different affordances.
    case 'image_refused':
      return <ImageRefusedScreen envelope={envelope} onRefetch={refetch} />;
    default: {
      // Sebastian ruling 2026-08-28: a status the switch does not
      // map must be LOUD, not invisible. The anchor_approved gap
      // sat latent 16 days because the failure mode was an empty
      // page nobody saw. Any future gap of the same class now
      // (a) shows the customer a neutral status screen instead of a
      // blank div — they see SOMETHING while they wait/refresh, and
      // (b) fires a console.warn naming the unhandled status, so a
      // browser session on a stuck order surfaces the defect in
      // DevTools + any log aggregator that captures console.
      //
      // Block 2 commit 2a (Sebastian ruling 2026-08-31 evening)
      // closed all eleven previously-unmapped statuses. After c2a
      // the only backend enum value that reaches this default is
      // `draft_configuring`, which never routes to /book/:orderId
      // (the configurator lives at /book). In practice: nothing
      // reaches here today — the console.warn IS the alert for a
      // future schema addition without a matching switch case.
      //
      // Block 2 commit 2b Part 2 (Sebastian ruling 2026-08-31
      // evening) replaces the pre-c2b fallback (`WaitScreen` with
      // `wait.anchorHeading` = "Wir zeichnen dein Titelbild.") with
      // the neutral `status.unknown` copy on BookStatusInfoScreen.
      // Chosen for being true in every past-render AND pre-render
      // state; the pre-c2b copy was true only on pre-render states.
      // The `data-testid="book-route"` wrapper stays so tests using
      // getByTestId('book-route') keep working; DOM shape is stable
      // while the visible content becomes honest.
      // eslint-disable-next-line no-console
      console.warn(
        `[book-route] Unhandled envelope.status="${envelope.status}" for orderId="${orderId ?? ''}" — falling through to generic wait. This warn IS the alert; a status the switch does not map should be loud, not invisible.`,
      );
      return (
        <div data-testid="book-route" data-order-id={orderId ?? ''}>
          <BookStatusInfoScreen
            heading={STRINGS.book.status.unknown.heading}
            subtitle={STRINGS.book.status.unknown.subtitle}
          />
        </div>
      );
    }
  }
}
